import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { fetchVehicleDataThunk } from "store/thunks/vehicleSpecThunks";

// Vehicle Brand Interface
interface VehicleBrand {
	id: number;
	name: string;
	country: string;
	founded_year: number;
	deleted: number;
}

// Vehicle Type Interface
interface VehicleType {
	id: number;
	title_en: string;
	desc_en: string;
	title_ru: string;
	desc_ru: string;
	title_tk: string;
	desc_tk: string;
	title_de?: string;
	desc_de?: string;
	title_ar?: string;
	desc_ar?: string;
	title_es?: string;
	desc_es?: string;
	title_fr?: string;
	desc_fr?: string;
	title_zh?: string;
	desc_zh?: string;
	title_ja?: string;
	desc_ja?: string;
	deleted: number;
}

// Vehicle Model Interface
interface VehicleModel {
	id: number;
	brand_id: number;
	name: string;
	year_start: number;
	year_end?: number;
	deleted: number;
}

// Define Vehicle Data Structure
export interface VehicleData {
	brands: VehicleBrand[];
	types: VehicleType[];
	models: VehicleModel[];
}

// Define Redux State Type
interface VehicleState {
	data: VehicleData;
	loading: boolean;
	error: string | null;
}

// Initial State
const initialState: VehicleState = {
	data: {
		brands: [],
		types: [],
		models: [],
	},
	loading: false,
	error: null,
};

// Vehicle Slice
const vehicleSlice = createSlice({
	name: "vehicles",
	initialState,
	reducers: {
		clearVehicleData: (state) => {
			state.data = { brands: [], types: [], models: [] };
			state.error = null;
		},
	},
	extraReducers: (builder) => {
		builder.addCase(fetchVehicleDataThunk.pending, (state) => {
			state.loading = true;
			state.error = null;
		});
		builder.addCase(
			fetchVehicleDataThunk.fulfilled,
			(state, action: PayloadAction<VehicleData>) => {
				state.loading = false;
				state.data = action.payload;
			},
		);
		builder.addCase(fetchVehicleDataThunk.rejected, (state, action) => {
			state.loading = false;
			state.error =
				typeof action.payload === "string"
					? action.payload
					: "Failed to fetch vehicle data.";
		});
	},
});

export const { clearVehicleData } = vehicleSlice.actions;
export default vehicleSlice.reducer;
